import React, { Component, useEffect, useState } from "react"
import { useResponsiveSize } from "@/hooks"
import { connect, ConnectedProps } from "react-redux"

import classNames from "classnames"

import { Tooltip } from "@quorumus/plum"
import { Icon } from "quorumdesign"

// shared modules
import { openFilterGroup, toggleFilterGroup } from "shared/search/action-creators"
import {
    selectHasRelatedActiveFilters,
    selectIsFilterGroupOpen,
    selectPermissionedFilterComponents,
} from "shared/search/selectors"

import { ExpandableDropdown } from "app/static/frontend/search-new/components/FilterGroup/ExpandableDropdown"

interface SearchFilterGroupProps {
    alwaysOpen?: boolean
    dataCy?: string
    displayLabel?: string
    helpText?: string
    isProfile?: boolean
    noHeader?: boolean
    searchFilterTerm?: string // selected from the search => uniqueFlvId slice of the store
    uniqueFilterGroupId: string
    uniqueFlvId: string
}

const UnconnectedSearchFilterGroup = ({
    alwaysOpen,
    dataCy,
    displayLabel,
    hasActiveFilter,
    helpText,
    isOpen,
    isProfile,
    noHeader,
    openFilterGroup,
    renderedFilters,
    searchFilterTerm,
    toggleFilterGroup,
    uniqueFilterGroupId,
    uniqueFlvId,
}: SearchFilterGroupProps & StateProps & DispatchProps) => {
    const [filterGroupFilterChildrenContainSearchFilterTerm, setFilterGroupFilterChildrenContainSearchFilterTerm] =
        useState(false)
    const [shouldUnmountFilterGroup, setShouldUnmountFilterGroup] = useState(false)
    const [filterGroupContainsSearchFilterTerm, setFilterGroupContainsSearchFilterTermState] = useState(false)

    const openFilterGroupAction = openFilterGroup
    const toggleFilterGroupAction = toggleFilterGroup

    useEffect(() => {
        // On change of SearchFilterTerm, reset the search term state so that every Filter re-evaluates
        // whether it has the new search term or not
        setFilterGroupFilterChildrenContainSearchFilterTerm(false)
        setFilterGroupContainsSearchFilterTermState(false)
        setShouldUnmountFilterGroup(false)
    }, [searchFilterTerm])

    useEffect(() => {
        if (alwaysOpen && !isOpen) {
            toggleFilterGroupAction(uniqueFlvId, uniqueFilterGroupId)
        }
    }, [alwaysOpen, isOpen, uniqueFlvId, uniqueFilterGroupId, toggleFilterGroupAction])

    useEffect(() => {
        // we want to open the FilterGroup when searchFilterTerm matches displayLabel
        // but filterGroupContainsSearchFilterTerm is false
        // (i.e., when the FilterGroup ExpandableDropdown title matches the search term,
        // but none of its FilterComponent children do)
        if (
            searchFilterTerm &&
            !filterGroupContainsSearchFilterTerm &&
            displayLabel &&
            displayLabel.toLowerCase().includes(searchFilterTerm.toLowerCase())
        ) {
            openFilterGroupAction(uniqueFlvId, uniqueFilterGroupId)
            setFilterGroupContainsSearchFilterTermState(true)
            setShouldUnmountFilterGroup(false)
        }
    }, [
        displayLabel,
        filterGroupContainsSearchFilterTerm,
        openFilterGroupAction,
        searchFilterTerm,
        uniqueFilterGroupId,
        uniqueFlvId,
    ])

    useEffect(() => {
        if (searchFilterTerm && !filterGroupContainsSearchFilterTerm) {
            setShouldUnmountFilterGroup(true)
        }
    }, [searchFilterTerm, shouldUnmountFilterGroup, filterGroupContainsSearchFilterTerm])

    const setFilterGroupContainsSearchFilterTerm = (hasTerm) => {
        if (hasTerm) {
            setFilterGroupContainsSearchFilterTermState(true)
            // we use filterGroupFilterChildrenContainSearchFilterTerm to denote that at least one of the
            // FilterComponent children contain the searchFilterTerm
            setFilterGroupFilterChildrenContainSearchFilterTerm(true)
            openFilterGroupAction(uniqueFlvId, uniqueFilterGroupId)
        }
    }

    const renderFilters = () => {
        const shouldAlwaysDisplayFilter =
            // when we have a search term,
            searchFilterTerm &&
            displayLabel &&
            // the ExpandableDropdown title contains the search term
            displayLabel.toLowerCase().includes(searchFilterTerm.toLowerCase()) &&
            // and none of the current renderFilters FilterComponent children contain the search term
            !filterGroupFilterChildrenContainSearchFilterTerm

        return renderedFilters.map((item) => {
            const FilterComponent = item
            // uniqueFilterId is a Redux key and is not guaranteed unique, since filters may share state. For example, Party
            // is divided into two sections: checkboxes for major parties, and a dropdown of obscure parties.
            const key = `${item.defaultProps.uniqueFilterId}${item.defaultProps.componentKey || ""}`

            return (
                <FilterComponent
                    isProfile={isProfile}
                    key={key}
                    searchFilterTerm={searchFilterTerm}
                    setFilterGroupContainsSearchFilterTerm={setFilterGroupContainsSearchFilterTerm}
                    shouldAlwaysDisplayFilter={shouldAlwaysDisplayFilter}
                    uniqueFilterGroupId={uniqueFilterGroupId}
                    uniqueFlvId={uniqueFlvId}
                />
            )
        })
    }

    const { isMobile } = useResponsiveSize()

    const toggleThisFilterGroup = () => toggleFilterGroupAction(uniqueFlvId, uniqueFilterGroupId)

    if (noHeader) {
        return (
            // keeping old code for filters with noHeader for now
            <div
                className={classNames("panel", "panel-default", {
                    "filter-group": !alwaysOpen,
                    "always-open": alwaysOpen,
                })}
                data-cy={dataCy}
            >
                {isOpen && renderFilters()}
            </div>
        )
    }
    return (
        <ExpandableDropdown
            alwaysOpen={alwaysOpen}
            dataCy={dataCy}
            filterGroupContainsSearchFilterTerm={filterGroupContainsSearchFilterTerm}
            hasActiveChild={hasActiveFilter}
            helpText={helpText}
            isExpanded={isOpen}
            searchFilterTerm={searchFilterTerm}
            shouldUnmountFilterGroup={shouldUnmountFilterGroup}
            title={displayLabel}
            toggleFilterGroup={toggleThisFilterGroup}
            tooltipElement={
                !isMobile && (
                    <Tooltip label={helpText}>
                        <Icon icon="info-circle" iconFamily="far" />
                    </Tooltip>
                )
            }
        >
            {renderFilters()}
        </ExpandableDropdown>
    )
}

const mapStateToProps = (state, props) => ({
    // @ts-expect-error These selectors are improperly typed with the current version of react-redux
    hasActiveFilter: selectHasRelatedActiveFilters(state, props),
    // @ts-expect-error These selectors are improperly typed with the current version of react-redux
    isOpen: selectIsFilterGroupOpen(state, props),
    // @ts-expect-error These selectors are improperly typed with the current version of react-redux
    renderedFilters: selectPermissionedFilterComponents(state, props),
})

interface StateProps {
    hasActiveFilter: boolean
    isOpen: boolean
    renderedFilters: any[]
}

interface DispatchProps {
    openFilterGroup: (arg0: string, arg1: string) => void
    toggleFilterGroup: (arg0: string, arg1: string) => void
}

export const SearchFilterGroup = connect<StateProps, DispatchProps, SearchFilterGroupProps>(mapStateToProps, {
    openFilterGroup,
    toggleFilterGroup,
})(UnconnectedSearchFilterGroup)
